import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  forwardRef,
  inject,
  viewChild,
  input,
  output,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Permissions } from '@app/core/guard/Permissions';
import { PermissionsService } from '@services/helpers/permissions.service';
import { ModalBankKitRequestComponent } from '@features/dashboard/commercial/pages/deal-detail/components/modal-bank-kit-request/modal-bank-kit-request.component';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { CompanyDataRoomComponent } from '@shared/components/company-data-room/company-data-room.component';
import { provideUnscopedCompanyScope } from '@services/helpers/unscoped-company-scope';
import { CompanyService } from '@services/http/company.service';
import { CorporateCharterHttpService } from '@services/http/corporate-charter/corporate-charter-http.service';
import { DataRoomHttpService } from '@services/http/data-room/data-room-http.service';
import { DataRoomIntelligenceHttpService } from '@services/http/data-room/data-room-intelligence-http.service';
import { DataRoomGroupHttpService } from '@services/http/data-room/data-room-group-http.service';
import { DataRoomBankShareHttpService } from '@services/http/data-room/data-room-bank-share-http.service';
import { DataRoomContactShareHttpService } from '@services/http/data-room/data-room-contact-share-http.service';
import { DataRoomEmailDispatchHttpService } from '@services/http/data-room/data-room-email-dispatch-http.service';
import { DataRoomReassignmentHttpService } from '@services/http/data-room/data-room-reassignment-http.service';
import { DataRoomInboxHttpService } from '@services/http/data-room/data-room-inbox-http.service';
import { DataRoomZipHttpService } from '@services/http/data-room/data-room-zip-http.service';
import { CompanyContactHttpService } from '@services/http/companys/company-contact-http.service';
import { BankRelationshipHttpService } from '@services/http/bank-relationship/bank-relationship-http.service';
import { AccountingOcrReadingsHttpService } from '@services/http/accounting-document-management/accounting-ocr-readings-http.service';
import { AccountingExtractionProgressHttpService } from '@services/http/accounting-document-management/accounting-extraction-progress-http.service';
import { BankRelationshipFacadeService } from '@app/core/services/facades/bank-relationship/bank-relationship-facade.service';
import { BankShareActionsService } from '@features/dashboard/market/data-room/bank-share/bank-share-actions.service';
import { DataRoomDocumentLinkService } from '@services/helpers/data-room-document-link/data-room-document-link.service';
import { DeParaOverwriteGuardService } from '@services/helpers/de-para-overwrite/de-para-overwrite-guard.service';
import { DataRoomDocumentTypeCatalogService } from '@features/dashboard/market/data-room/services/data-room-document-type-catalog.service';
import { DataRoomUploadDuplicateCheckService } from '@features/dashboard/market/data-room/services/data-room-upload-duplicate-check.service';
import { DataRoomIntelligenceActionsService } from '@features/dashboard/market/data-room/services/data-room-intelligence-actions.service';
import { DataRoomDocumentDeletionService } from '@features/dashboard/market/data-room/services/data-room-document-deletion.service';
import { DataRoomFilePreviewService } from '@features/dashboard/market/data-room/services/data-room-file-preview.service';
import { DataRoomInboxReadAgainService } from '@features/dashboard/market/data-room/services/data-room-inbox-read-again.service';
import { DataRoomInboxFileViewerService } from '@features/dashboard/market/data-room/services/data-room-inbox-file-viewer.service';
import { AccountingDataStandardizationService } from '@services/http/accounting-data-standardization/accounting-data-standardization.service';
import { DataRoomFolderComponent } from '@features/dashboard/market/data-room/components/data-room-folder/data-room-folder.component';
import CompanyCompletenessPageComponent from '@features/dashboard/market/data-room/pages/company-completeness/company-completeness.component';
import { DATA_ROOM_NAVIGATOR } from '@features/dashboard/market/data-room/navigation/data-room-navigator';
import { InMemoryDataRoomNavigator } from '@features/dashboard/market/data-room/navigation/in-memory-data-room-navigator';
import {
  DATA_ROOM_LINK_HANDLER,
  DataRoomLinkHandler,
} from '@features/dashboard/market/data-room/utils/data-room-link-handler';
import { DialogService } from 'primeng/dynamicdialog';

/**
 * Aba **Documentos** da tela do deal (card 3281, T04; PBI 3800).
 *
 * Reunião de 02/10/2026: *"o Data Room tem que ser mesmo o repositório"*. A aba abre no **Data Room da
 * empresa do deal**, o mesmo componente da rota do Data Room (`app-company-data-room`, card 3248), com a
 * `_TRIAGEM`: o arquivo solto ali é identificado pelo Cog, renomeado, guardado na pasta certa e, se for
 * contábil, segue para a extração.
 *
 * **Os links do Data Room ficam no deal**, sem iframe (PBI 3800). A aba provê o `InMemoryDataRoomNavigator` como
 * `DATA_ROOM_NAVIGATOR` e como destino do {@link DATA_ROOM_LINK_HANDLER}: abrir uma pasta mostra o mesmo componente
 * da rota do mercado (`app-data-room-folder`) aqui dentro; a completude também; o Societário e os duplicados abrem
 * em nova aba. A pasta e o documento abertos ficam na URL do deal (`?tab=documents&folder=&doc=`). Até o PBI 3800
 * a pasta abria num iframe da própria tela (ADR do card 3286).
 *
 * Quem não é do time interno (o parceiro) vê o Data Room em **visão restrita**: sem compartilhamento com
 * banco e sem o link do Societário, e só envia arquivo pela `_TRIAGEM` (pelo endpoint do deal). O que ele pode ler
 * é decidido no backend (permissões do grupo "Parceiro - Onboarding" e escopo de empresas do parceiro), não aqui.
 *
 * **Ignora o escopo de empresas do TENANT** (card 3298), como as outras abas do deal: o deal aparece em
 * qualquer escopo, então o Data Room dele também. ⚠️ Serviço HTTP novo usado dentro da raiz, da pasta ou da
 * completude precisa entrar na lista abaixo (ver `provideUnscopedCompanyScope`). O `DialogService` também é provido
 * aqui: as modais abertas por ele (Mover, visualização) pegam os serviços desta subárvore, sem o escopo.
 */
@Component({
  selector: 'app-deal-data-room-tab',
  standalone: true,
  imports: [
    CompanyDataRoomComponent,
    DataRoomFolderComponent,
    CompanyCompletenessPageComponent,
    ModalBankKitRequestComponent,
  ],
  providers: [
    ...provideUnscopedCompanyScope([
      DataRoomHttpService,
      DataRoomIntelligenceHttpService,
      DataRoomGroupHttpService,
      DataRoomBankShareHttpService,
      DataRoomContactShareHttpService,
      DataRoomEmailDispatchHttpService,
      DataRoomReassignmentHttpService,
      DataRoomInboxHttpService,
      DataRoomZipHttpService,
      CompanyContactHttpService,
      BankRelationshipHttpService,
      AccountingOcrReadingsHttpService,
      DataRoomDocumentLinkService,
      DataRoomDocumentTypeCatalogService,
      DataRoomUploadDuplicateCheckService,
      DataRoomIntelligenceActionsService,
      DataRoomDocumentDeletionService,
      DataRoomFilePreviewService,
      DataRoomInboxReadAgainService,
      DataRoomInboxFileViewerService,
      DeParaOverwriteGuardService,
      AccountingDataStandardizationService,
      CorporateCharterHttpService,
      CompanyService,
      // Globais que chamam serviço HTTP: sem uma instância aqui, usariam o HTTP global, com o escopo (revisão PBI 3800).
      BankShareActionsService,
      BankRelationshipFacadeService,
      AccountingExtractionProgressHttpService,
    ]),
    DialogService,
    InMemoryDataRoomNavigator,
    { provide: DATA_ROOM_NAVIGATOR, useExisting: InMemoryDataRoomNavigator },
    {
      provide: DATA_ROOM_LINK_HANDLER,
      useExisting: forwardRef(() => DealDataRoomTabComponent),
    },
  ],
  templateUrl: './deal-data-room-tab.component.html',
  styleUrl: './deal-data-room-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DealDataRoomTabComponent implements OnInit, DataRoomLinkHandler {
  private readonly partnersHttp = inject(PartnersHttpService);

  /** Raiz, pasta ou completude: o que a aba mostra (e a URL do deal guarda). */
  protected readonly navigator = inject(InMemoryDataRoomNavigator);

  /** Deal da tela: o envio do parceiro vai por ele. */
  readonly dealId = input.required<string>();

  /** Empresa do deal. */
  readonly companyId = input.required<string>();

  /** Time interno da Legatus (`canDecide` do servidor). Falso = parceiro, Data Room em visão restrita. */
  readonly visaoInterna = input(false);

  /** Repassa ao pai o pedido de Kit Banco enviado, para recarregar a linha do tempo do deal. */
  readonly bankKitRequested = output<void>();

  /**
   * Envio da `_TRIAGEM` para o parceiro: pelo `POST partners/deals/{id}/documents`, que grava na mesma
   * `_TRIAGEM` da empresa (card 3238) e não exige `DATAROOM_CREATE`. O time interno sobe pelo caminho
   * normal do Data Room (`null`).
   */
  protected readonly triageUploader = computed(() =>
    this.visaoInterna()
      ? null
      : (file: File) => this.partnersHttp.uploadDealDocument(this.dealId(), file)
  );

  /** Pasta aberta na aba (quando a visão é uma pasta). */
  protected readonly openFolderId = computed(() => {
    const view = this.navigator.view();
    return view.kind === 'folder' ? view.folderId : null;
  });

  /**
   * Botão "Solicitar Kit Banco" (card 3753): time interno e parceiro do deal, com a permissão própria
   * `COMMERCIAL_REQUEST_BANK_KIT`, a mesma que o backend confere.
   */
  protected readonly canRequestBankKit = toSignal(
    inject(PermissionsService).hasPermission(Permissions.Commercial.REQUEST_BANK_KIT),
    { initialValue: false }
  );

  private readonly bankKitModal = viewChild(ModalBankKitRequestComponent);

  ngOnInit(): void {
    // Reabre a pasta e o documento que estão na URL do deal (F5, link copiado). A empresa é lida uma vez: trocar de deal
    // recria a aba (a tela do deal some e volta), então o navegador nasce de novo com a empresa nova. Se um dia a aba
    // sobreviver à troca de deal, isto precisa virar um `effect` sobre `companyId()`.
    // `folder`/`doc` ficam na URL ao trocar de aba do deal, de propósito: voltar a Documentos reabre o mesmo lugar.
    this.navigator.configure(this.companyId());
  }

  /**
   * {@link DataRoomLinkHandler}: links da raiz do Data Room (pasta, KPI, completude, busca) ficam na aba. O
   * `replaceUrl` não se aplica: a troca de visão já grava a URL do deal sem empilhar histórico.
   */
  open(commands: unknown[], queryParams?: Record<string, string | null | undefined>): void {
    this.navigator.navigate(commands, queryParams);
  }

  /** Abre a modal do pedido de Kit Banco ao cliente. */
  protected openBankKitRequest(): void {
    this.bankKitModal()?.open(this.dealId());
  }
}
